import { createContext, useCallback, useContext, useMemo } from 'react'

import { useChannel } from 'modules/Monitoring/hooks/useChannel'
import {
  FCWithChildren,
  QueueStatsFilterRequest,
  QueueStatsFilterResponse,
  QueueStatsFilterTypeDefinitionValue,
} from 'modules/Monitoring/types'
import { UrlStateStorageKeys } from 'modules/Monitoring/url-state-storage/consts'
import { useHashedSearchParams } from 'modules/Monitoring/url-state-storage/useHashedSearchParams'

import { AdditionalFilterRecord, AdditionalFilterValue } from './types'
import { DEFAULT_OPTION_KEY, getAllOptionsKey } from './utils/getAllOptionsKey'
import {
  convertAllFullySelectedFilterValuesToEmptyArray,
  prepareFiltersForRequest,
} from './utils/prepareFilters'

export type AdditionalFiltersContext = {
  additionalFilters: Partial<AdditionalFilterRecord>
  clearAdditionalFilters: VoidFunction
  preparedFiltersForRequest?: QueueStatsFilterRequest
  setAdditionalFilter: (key: string, value: AdditionalFilterValue) => void
  setAdditionalFilters: (value: Partial<AdditionalFilterRecord>) => void
  removeAdditionalFilters: (keys: string[]) => void
}

export const AdditionalFiltersContext = createContext<AdditionalFiltersContext>(
  {} as AdditionalFiltersContext,
)

export const useAdditionalFilters = () => {
  const context = useContext(AdditionalFiltersContext)

  if (!context) {
    throw new Error(
      'AdditionalFiltersContext cannot be used outside AdditionalFiltersContextProvider',
    )
  }
  return context
}

type AdditionalFiltersProviderProps = {
  initialFilters?: QueueStatsFilterResponse
}

const INITIAL_DATA = {}

export const AdditionalFiltersProvider: FCWithChildren<
  AdditionalFiltersProviderProps
> = ({ initialFilters, children }) => {
  const { regionedChannel } = useChannel()

  const {
    state: additionalFilters,
    updateState,
    clearState,
    removeKeys,
  } = useHashedSearchParams<AdditionalFilterRecord>({
    hashParamKey: UrlStateStorageKeys.AdditionalFilters,
    initialState: INITIAL_DATA,
    contextKey: regionedChannel,
  })

  const allOptionsMap = useMemo(() => {
    const newAllOptionsMap = new Map<string, QueueStatsFilterTypeDefinitionValue[]>()

    if (!initialFilters?.items) {
      return newAllOptionsMap
    }

    for (const item of initialFilters.items) {
      const optionKey = item.parent?.option || DEFAULT_OPTION_KEY
      const mapKey = getAllOptionsKey(item.key, optionKey)
      newAllOptionsMap.set(mapKey, item.values)
    }

    return newAllOptionsMap
  }, [initialFilters])

  const preparedFiltersForRequest = useMemo(() => {
    const filters = prepareFiltersForRequest(additionalFilters)

    if (Object.keys(filters).length === 0) {
      return undefined
    }

    const filtersWithEmptyArraysForAllSelectedOptions = initialFilters
      ? convertAllFullySelectedFilterValuesToEmptyArray(filters, allOptionsMap)
      : filters

    return Object.keys(filtersWithEmptyArraysForAllSelectedOptions).length
      ? filtersWithEmptyArraysForAllSelectedOptions
      : undefined
  }, [additionalFilters, initialFilters, allOptionsMap])

  const handleAdditionalFilterChange = useCallback(
    (key: string, value: AdditionalFilterValue) => {
      updateState({ [key]: value })
    },
    [updateState],
  )

  const handleAdditionalFiltersChange = useCallback(
    (newFilters: Partial<AdditionalFilterRecord>) => {
      updateState(newFilters)
    },
    [updateState],
  )

  const value = useMemo(
    () => ({
      additionalFilters,
      preparedFiltersForRequest,
      setAdditionalFilter: handleAdditionalFilterChange,
      setAdditionalFilters: handleAdditionalFiltersChange,
      removeAdditionalFilters: removeKeys,
      clearAdditionalFilters: clearState,
    }),
    [
      additionalFilters,
      preparedFiltersForRequest,
      handleAdditionalFilterChange,
      handleAdditionalFiltersChange,
      removeKeys,
      clearState,
    ],
  )

  return (
    <AdditionalFiltersContext.Provider value={value}>
      {children}
    </AdditionalFiltersContext.Provider>
  )
}
